import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { ProductShoppingCategory } from '../../api/models/product-shopping-category.models';
import { RootState } from '../store';

/**
 * Adapter
 * Use ProductShoppingCategoryId as the entity key
 */
const productShoppingCategoryAdapter = createEntityAdapter<ProductShoppingCategory>({
  selectId: (category) => category.ProductShoppingCategoryId,
});

/**
 * State
 */
interface ProductShoppingCategoryState {
  isLoading: boolean;
  categories: EntityState<ProductShoppingCategory>;
}

const initialState: ProductShoppingCategoryState = {
  isLoading: false,
  categories: productShoppingCategoryAdapter.getInitialState(),
};

/**
 * Slice
 */
export const productShoppingCategorySlice = createSlice({
  name: 'productShoppingCategory',
  initialState,
  reducers: {
    resetState: () => initialState,

    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Replace the entire category list
     * (order is preserved as sent by the API)
     */
    setCategories: (state, action: PayloadAction<ProductShoppingCategory[]>) => {
      productShoppingCategoryAdapter.setAll(state.categories, action.payload);
    },
  },
});

/**
 * Adapter selectors
 */
export const {
  selectAll: selectAllProductShoppingCategories,
  selectById: selectProductShoppingCategoryById,
  selectIds: selectProductShoppingCategoryIds,
} = productShoppingCategoryAdapter.getSelectors<RootState>((state) => state.productShoppingCategory.categories);

/**
 * Additional selectors
 */
export const selectIsProductShoppingCategoriesLoading = (state: RootState): boolean =>
  state.productShoppingCategory.isLoading;

export default productShoppingCategorySlice.reducer;
